import type { CatalogService, SupplierProfile } from '@/mock/types'

/** What the AI lookup returns: a filled-in profile and where it read it. */
export type FoundSupplier = {
  name: string
  category: string
  region?: string
  profile: SupplierProfile
  sources: string[]
}

export const normalize = (value: string) =>
  value
    .toLocaleLowerCase('cs')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .trim()

/** A pasted web or social link rather than a name. */
export const isLink = (value: string) =>
  /^(https?:\/\/)?([\w-]+\.)+[a-z]{2,}(\/\S*)?$/i.test(value.trim())

export function catalogMatches(catalog: CatalogService[], query: string) {
  const needle = normalize(query.replace(/^https?:\/\//, '').replace(/^www\./, ''))
  if (needle.length < 2) return []
  return catalog
    .filter((item) => normalize(item.name).includes(needle) || item.website.includes(needle))
    .slice(0, 3)
}

/** Mocked internet results the "AI" finds; anything else gets a profile guessed from the input. */
const internet: (FoundSupplier & { keys: string[] })[] = [
  {
    keys: ['lumen', 'lumenfoto'],
    name: 'Lumen – svatební fotografie',
    category: 'photographer',
    region: 'Jihomoravský kraj',
    profile: {
      description:
        'Dokumentární svatební fotografie Kláry Novotné. Fotí celý den od příprav po první tanec, ráda v přírodě a na statcích.',
      website: 'lumenfoto.cz',
      email: 'klara@lumenfoto.cz',
      phone: '+420 736 410 220',
      instagram: 'lumen.foto',
      facebook: 'lumenfoto',
    },
    sources: ['lumenfoto.cz', 'instagram.com/lumen.foto'],
  },
  {
    keys: ['statek', 'hruba skala', 'hrubaskala'],
    name: 'Statek Hrubá Skála',
    category: 'venue',
    region: 'Liberecký kraj',
    profile: {
      description:
        'Zrekonstruovaný statek v Českém ráji s loukou pro obřad, stodolou pro 140 hostů a ubytováním pro 60 lidí.',
      website: 'statekhrubaskala.cz',
      email: 'svatby@statekhrubaskala.cz',
      phone: '+420 481 389 100',
      address: 'Hrubá Skála 12, 511 01 Turnov',
      instagram: 'statek.hrubaskala',
    },
    sources: ['statekhrubaskala.cz', 'facebook.com/statekhrubaskala'],
  },
]

const categoryHints: [RegExp, string][] = [
  [/foto|photo/, 'photographer'],
  [/video|film|kamera/, 'videographer'],
  [/dj|music|hudba|kapela|band/, 'party-music'],
  [/kvet|flor|kytic/, 'florist'],
  [/zamek|statek|hotel|venue|mlyn|chateau/, 'venue'],
  [/catering|gastro|kuchyn/, 'catering'],
  [/dort|cukr|cake/, 'cake-sweets'],
  [/viza|makeup|liceni|glow/, 'makeup'],
]

/** Pretends to search the web: a known result for a few inputs, otherwise a guess from the name or link. */
export function lookUp(query: string): FoundSupplier {
  const needle = normalize(query)
  const known = internet.find((item) => item.keys.some((key) => needle.includes(key)))
  if (known) return known
  const instagram = query.match(/instagram\.com\/([\w.]+)/i)?.[1]
  const facebook = query.match(/facebook\.com\/([\w.]+)/i)?.[1]
  const domain = isLink(query)
    ? query
        .replace(/^https?:\/\//, '')
        .replace(/^www\./, '')
        .split('/')[0]
    : undefined
  const handle = instagram ?? facebook ?? domain?.split('.')[0] ?? query
  const name = handle
    .replace(/[._-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (first) => first.toLocaleUpperCase('cs'))
  const website = domain && !instagram && !facebook ? domain : undefined
  return {
    name,
    category: categoryHints.find(([pattern]) => pattern.test(needle))?.[1] ?? 'other',
    profile: {
      description: `Svatební dodavatel ${name}. Popis jsme sestavili z veřejných stránek, zkontrolujte ho.`,
      website,
      instagram,
      facebook,
    },
    sources: [
      website,
      instagram && `instagram.com/${instagram}`,
      facebook && `facebook.com/${facebook}`,
    ]
      .filter(Boolean)
      .map(String)
      .concat(website || instagram || facebook ? [] : ['vyhledávání Google']),
  }
}
